import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useLocalStorageValue } from '@react-hookz/web';
import {
  ArrowsClockwiseIcon,
  DotsThreeVerticalIcon,
  EnvelopeIcon,
} from '@phosphor-icons/react';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SystemEntity } from '@erp/shared-types';
import {
  EntityDetailTabsProvider,
  EntityDetailTabsHeader,
  EntityDetailTabsContent,
  useEntityDetailTabsContext,
} from '@/components/system-entities/entity-detail-tabs';
import { useChatPanelActive } from '@/hooks/useChatPanelActive';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { useRefreshArInvoiceFromFamous } from '@/api/rpc/hooks/accounts-receivable/useRefreshArInvoiceFromFamous';
import { getInvoiceStatusBadge } from '@/components/accounts-receivable/invoice-status-badge';
import { SendInvoiceDialog } from './SendInvoiceDialog';
// AR Vision Demo (GEN-2247) — fixture-backed invoice view (Scene 2). Delete
// with src/demo/ar.
import { isArDemoMode } from '@/demo/ar/demo-mode';
import { DemoInvoiceDetail } from '@/demo/ar/DemoInvoiceDetail';

interface ARInvoiceDetailSlideinProps {
  invoiceId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Inner wrapper rendered within the EntityDetailTabsProvider. Lays out the
 * invoice title + status + actions, then the related-entity tab bar, then the
 * active tab's content (the invoice itself plus any linked sales order,
 * shipment, payments, and email threads).
 */
function ARInvoiceDetailTabs({
  invoiceId,
  onSend,
}: {
  invoiceId: string;
  onSend: () => void;
}) {
  const gt = useGT();
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  const { invoice, isLoading } = useArInvoice(invoiceId);
  const refreshFromFamous = useRefreshArInvoiceFromFamous();
  const isFamousInvoice = invoice?.originSystem === 'famous';

  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex flex-col flex-1 min-h-0"
    >
      <SheetHeader className="shrink-0 grow-0">
        <div className="flex items-center gap-2">
          {isLoading ? (
            <SheetTitle>
              <T context="loading state title">Loading...</T>
            </SheetTitle>
          ) : invoice ? (
            <>
              <SheetTitle>
                {invoice.invoiceNumber ??
                  gt('(No invoice number)', {
                    context: 'placeholder when invoice has no number',
                  })}
              </SheetTitle>
              {getInvoiceStatusBadge(invoice.status)}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-auto"
                    aria-label={gt('Invoice actions', {
                      context: 'button label to open invoice action menu',
                    })}
                  >
                    <DotsThreeVerticalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onSend}>
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    <T context="menu item to send invoice email">
                      Send Invoice
                    </T>
                  </DropdownMenuItem>
                  {isFamousInvoice && (
                    <DropdownMenuItem
                      onClick={() =>
                        refreshFromFamous.mutate({ id: invoiceId })
                      }
                      disabled={refreshFromFamous.isPending}
                    >
                      <ArrowsClockwiseIcon
                        className={`h-4 w-4 mr-2 ${refreshFromFamous.isPending ? 'animate-spin' : ''}`}
                      />
                      <T context="menu item to refresh invoice data from Famous ERP">
                        Refresh from Famous
                      </T>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <SheetTitle>
              <T>Invoice not found</T>
            </SheetTitle>
          )}
        </div>
        <EntityDetailTabsHeader />
      </SheetHeader>
      <div className="flex-1 min-h-0 overflow-hidden">
        <EntityDetailTabsContent />
      </div>
    </Tabs>
  );
}

export function ARInvoiceDetailSlidein({
  invoiceId,
  isOpen,
  onOpenChange,
}: ARInvoiceDetailSlideinProps) {
  const gt = useGT();
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('ar-invoice-detail-view-size', {
      defaultValue: 50,
    });

  const { invoice } = useArInvoice(invoiceId);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  // While the AI chat panel is open, drop modal behavior so the docked chat
  // stays clickable/typeable and clicks into it don't dismiss this slide-in.
  const chatActive = useChatPanelActive();

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange} modal={!chatActive}>
      <SheetContentResizable
        // Dock to the left of the global Nami chat panel when it's open so the
        // two right-docked panels don't overlap. `--agent-panel-width` is set by
        // FloatingAgentPanel (0px when the panel is closed), so this is a no-op
        // whenever the chat panel isn't open.
        style={{
          right: 'var(--agent-panel-width, 0px)',
          width: 'calc(100% - var(--agent-panel-width, 0px))',
        }}
        // Clicking into the Nami chat panel (docked to the right of this
        // slide-in) registers as an outside interaction; don't let it dismiss
        // the slide-in.
        onInteractOutside={(e) => {
          const target = e.detail.originalEvent.target;
          if (
            target instanceof HTMLElement &&
            target.closest('[data-agent-panel]')
          ) {
            e.preventDefault();
          }
        }}
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        {/* AR Vision Demo (GEN-2247): serve the fixture-backed invoice view
            (Scene 2) so the Emails tab shows the sent email + branded
            letterhead without any backend calls. Delete with src/demo/ar. */}
        {invoiceId && isArDemoMode() ? (
          <DemoInvoiceDetail invoiceId={invoiceId} />
        ) : invoiceId ? (
          <EntityDetailTabsProvider
            initialEntity={{
              entityType: SystemEntity.AR_INVOICE,
              entityId: invoiceId,
            }}
          >
            <ARInvoiceDetailTabs
              invoiceId={invoiceId}
              onSend={() => setSendDialogOpen(true)}
            />
          </EntityDetailTabsProvider>
        ) : (
          <>
            <SheetHeader className="shrink-0 grow-0">
              <SheetTitle>
                <T context="title for the AR invoice detail slide-in panel">
                  Invoice Details
                </T>
              </SheetTitle>
            </SheetHeader>
            <div className="p-4 text-sm text-muted-foreground">
              <T context="placeholder text shown when no invoice is selected">
                No invoice selected
              </T>
            </div>
          </>
        )}

        <SheetFooter className="shrink-0 grow-0 flex items-end border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-fit"
          >
            <T context="button label to close invoice detail panel">Close</T>
          </Button>
        </SheetFooter>
      </SheetContentResizable>

      {invoice && (
        <SendInvoiceDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          invoiceId={invoiceId}
          customerId={invoice.customerId}
          customerName={invoice.customerName}
          invoiceNumber={invoice.invoiceNumber}
        />
      )}
    </Sheet>
  );
}
